import {
  CustomProductOptionConfigType,
  IManagerSupplierToShow,
  IProduct,
  IProductFileWithFile,
  IShow,
  ISupplierCompany,
  ISupplierCompanyToProductCategory,
  ISurcharge,
  SurchargeType,
} from "@evennt-tech/api-v2-core";
import {
  IGetOneOrganiserCompanyRes,
  IProductCustomConfigsSummary,
} from "@evennt-tech/api-v2-external";
import { parseISO } from "date-fns";
import {
  calculatePaymentFeeAmount,
  calculatePlatformFeeAmount,
  calculateProductPrice,
  calculateProductPriceWithFees,
  expandApiProductFees,
  expandProductColors,
  FetchedProductColour,
  isProductAvailableQuantity,
  mapExtras,
} from "hooks/helpers";
import { ProductConfigsBySupplier } from "store/custom-product-option/custom-product-option-shop.store";
import { unpackInlineProductConfigs } from "store/custom-product-option/utils/helpers";
import filterNotEmptyArray from "tools/common/filterNotEmptyArray";
import { collectProductFiles } from "tools/product/collectProductFiles";
import { getCustomConfigImage } from "tools/custom-product-option/getCustomConfigImage";
import { hasLazyCustomConfigs } from "tools/custom-product-option/customConfigsSummary";
import { getSingleProductImage } from "tools/custom-product-option/getSingleProductImage";
import expandCustomConfig from "tools/product/expandCustomConfig";
import { getMergedProjectCode } from "tools/projectCode";
import { FullProduct, ProductConfig, ShowProductConfig } from "ts/api/product";

export type ShowProductContext = {
  show: IShow;
  showId: string;
  organiserCompany: IGetOneOrganiserCompanyRes;
  supplierCompanies: Map<string, ISupplierCompany>;
  showManagers: Map<string | undefined, IManagerSupplierToShow>;
  categoryCodesBySupplier: Map<string, ISupplierCompanyToProductCategory[]>;
  productConfigsBySupplier?: ProductConfigsBySupplier;
  colors?: FetchedProductColour;
  tagIds?: Set<string>;
};

type ExpandShowProductParams = {
  product: IProduct & { customConfigsSummary?: IProductCustomConfigsSummary };
  configs: ProductConfig[];
  files?: IProductFileWithFile[];
  recommendedProducts?: FullProduct[];
};

type ExpandShowCustomConfigContext = {
  defaultConfig: Pick<ProductConfig, "priceUplift" | "showPrice">;
  supplierFee: number;
  organiserCompany: Pick<IGetOneOrganiserCompanyRes, "fee" | "paymentFee">;
  surcharges: ISurcharge[];
  buildUp: Date;
  files: IProductFileWithFile[];
};

/** A CUSTOM config priced for the show, with its image. */
export const expandShowCustomConfig = (
  config: ProductConfig,
  {
    defaultConfig,
    supplierFee,
    organiserCompany,
    surcharges,
    buildUp,
    files,
  }: ExpandShowCustomConfigContext
): ShowProductConfig => {
  const configImage = getCustomConfigImage(config, files);

  return {
    ...expandCustomConfig(config, {
      defaultPrice: defaultConfig.priceUplift,
      defaultShowPrice: defaultConfig.showPrice,
      supplierFee,
      organiserFee: organiserCompany.fee ?? 0,
      paymentFee: organiserCompany.paymentFee,
      surcharges,
      buildUp,
    }),
    ...(configImage && { image: configImage }),
  };
};

export const expandShowProduct = (
  {
    product,
    configs,
    files = [],
    recommendedProducts = [],
  }: ExpandShowProductParams,
  {
    show,
    showId,
    organiserCompany,
    supplierCompanies,
    showManagers,
    categoryCodesBySupplier,
    colors,
    tagIds,
  }: ShowProductContext
): FullProduct | null => {
  const supplierCompanyId = product?.supplierCompanyId;

  if (!product || !supplierCompanyId || !configs.length) {
    return null;
  }

  const defaultConfig = configs.find(
    ({ type }) => type === CustomProductOptionConfigType.DEFAULT
  );

  if (!defaultConfig) {
    return null;
  }

  const buildUp = parseISO(show.deliveryStart);
  const productSurcharges = (product.surcharges ?? []).filter(
    (surcharge) => surcharge.productId === product.id
  );

  const rawPrice = calculateProductPrice(
    buildUp,
    productSurcharges,
    defaultConfig.priceUplift,
    defaultConfig.showPrice
  );

  const isUnpublishedBySurcharge = rawPrice === SurchargeType.unpublish;
  const price = isUnpublishedBySurcharge ? 0 : rawPrice;

  const { customConfigsSummary } = product;
  const isAvailable =
    !defaultConfig.enabled &&
    customConfigsSummary &&
    hasLazyCustomConfigs(customConfigsSummary, configs)
      ? customConfigsSummary.hasAvailable
      : isProductAvailableQuantity(configs);

  if (!isAvailable) {
    return null;
  }

  const owner = supplierCompanies.get(supplierCompanyId);
  const supplierCompanyShowManager = showManagers.get(supplierCompanyId);

  if (!owner || !supplierCompanyShowManager) {
    return null;
  }

  const supplierFee = supplierCompanyShowManager.organiserFee ?? 0;

  const purchasedQuantity = defaultConfig.purchasedQuantity ?? 0;
  const quantity = defaultConfig.showQuantity ?? defaultConfig.quantity;

  product.productToTags?.forEach(({ tagId }) => tagIds?.add(tagId));

  if (colors) {
    expandProductColors(configs, colors);
  }

  const priceWithFees = price
    ? calculateProductPriceWithFees(price, organiserCompany.fee, supplierFee)
    : 0;

  const paymentFeeAmount =
    organiserCompany.paymentFee && price
      ? calculatePaymentFeeAmount(price, organiserCompany.paymentFee)
      : null;

  const platformFeeAmount =
    organiserCompany.fee && price
      ? calculatePlatformFeeAmount(price, organiserCompany.fee)
      : null;

  const projectCode = getMergedProjectCode({
    categories: product.categories ?? [],
    show,
    supplierCompany: owner,
    supplierCategoryCodes: categoryCodesBySupplier.get(owner.id) || [],
  });

  const image =
    getSingleProductImage(files, product.id, defaultConfig.primaryImageId) ??
    files[0];

  const expandedConfigs = configs.map(
    (config): ShowProductConfig => {
      if (config.type === CustomProductOptionConfigType.DEFAULT) {
        return {
          ...config,
          ...(platformFeeAmount !== null && { platformFeeAmount }),
          paymentFeeAmount,
          priceWithFees,
          price,
          purchasedQuantity: config.purchasedQuantity ?? 0,
        };
      }

      return expandShowCustomConfig(config, {
        defaultConfig,
        supplierFee,
        organiserCompany,
        surcharges: productSurcharges,
        buildUp,
        files,
      });
    }
  );

  const productToShow = product.productToShows?.find(
    (item) => item?.showId === showId
  );

  return {
    ...product,
    recommendedProducts,
    fees: expandApiProductFees(product, {
      platformFee: organiserCompany.fee,
      organiserFee: supplierFee,
      paymentFee: organiserCompany.paymentFee,
    }),
    tagIds: product.productToTags?.map(({ tagId }) => tagId) ?? [],
    priceWithFees,
    paymentFeeAmount,
    ...(platformFeeAmount && { platformFeeAmount }),
    price,
    basePrice: defaultConfig.priceUplift,
    surcharges: productSurcharges,
    ...(typeof defaultConfig.showPrice === "number" && {
      showPrice: defaultConfig.showPrice,
    }),
    quantity,
    extras: product.productToExtras
      ? mapExtras(product.productToExtras, organiserCompany, supplierFee)
      : [],
    owner: product.supplierCompany,
    ownerToShow: supplierCompanyShowManager,
    surcharge: product.surcharges,
    isVisible: isUnpublishedBySurcharge ? false : productToShow?.isVisible,
    hideToBuildTypeIds:
      productToShow?.hideBuildTypes?.map(({ buildTypeId }) => buildTypeId) ??
      [],
    purchasedQuantity,
    projectCode,
    configs: expandedConfigs,
    ...(image && { image }),
    organiserFee: organiserCompany.fee,
    purchaseLimit: product.purchaseLimit ?? null,
  } as FullProduct;
};

export const expandRecommendedProducts = (
  product: IProduct,
  context: ShowProductContext
): FullProduct[] =>
  (product.recommendedProducts ?? [])
    .map(({ recommendedProduct }) => recommendedProduct)
    .filter((recommended): recommended is IProduct => !!recommended)
    .map((recommended) => {
      const configsAndFiles = context.productConfigsBySupplier
        ?.get(recommended.supplierCompanyId)
        ?.get(recommended.id);

      return expandShowProduct(
        {
          product: recommended,
          configs: configsAndFiles?.configs?.length
            ? configsAndFiles.configs
            : unpackInlineProductConfigs(
                recommended.customProductOptionConfigs,
                context.showId
              ),
          files: configsAndFiles?.files?.length
            ? configsAndFiles.files
            : collectProductFiles(recommended),
        },
        context
      );
    })
    .filter(filterNotEmptyArray);

export default expandShowProduct;
